import FilterDate from '@/Enums/FilterDate.enum';
import type { ShortcutItem } from '@/Interfaces/ShortcutItem.interface';
import moment, { Moment } from 'moment/moment.js';

export const DATE_DISPLAY_PLACEHOLDER = 'MMM D, YYYY';
export const RANGE_DISPLAY_PLACEHOLDER = [DATE_DISPLAY_PLACEHOLDER, DATE_DISPLAY_PLACEHOLDER].join(' - ');

export const DATE_RANGE_FORMAT_LABELS: Record<FilterDate, string> = {
  [FilterDate.TODAY]: 'Today',
  [FilterDate.YESTERDAY]: 'Yesterday',
  [FilterDate.LAST_7_DAYS]: 'Last 7 Days',
  [FilterDate.LAST_30_DAYS]: 'Last 30 Days',
  [FilterDate.THIS_MONTH]: 'This Month',
  [FilterDate.LAST_MONTH]: 'Last Month',
  [FilterDate.THIS_YEAR]: 'This Year',
  [FilterDate.ALL_TIME]: 'All Time',
  [FilterDate.CUSTOM_RANGE]: 'Custom',
};

export const DATE_PICKER_SHORTCUTS: { [key: string]: ShortcutItem } = {
  TODAY: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.TODAY],
    value: FilterDate.TODAY,
    getValue: () => {
      const today = moment();
      return [today, today];
    },
  },
  YESTERDAY: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.YESTERDAY],
    value: FilterDate.YESTERDAY,
    getValue: () => {
      const yesterday = moment().subtract(1, 'days');
      return [yesterday, yesterday];
    },
  },
  LAST_7_DAYS: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.LAST_7_DAYS],
    value: FilterDate.LAST_7_DAYS,
    getValue: () => {
      const yesterday = moment().subtract(1, 'days');
      const sevenDaysAgo = moment().subtract(7, 'days');
      return [sevenDaysAgo, yesterday];
    },
  },
  LAST_7_DAYS_INCLUSIVE: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.LAST_7_DAYS],
    value: FilterDate.LAST_7_DAYS,
    getValue: () => {
      const today = moment();
      const sevenDaysAgo = moment().subtract(6, 'days');
      return [sevenDaysAgo, today];
    },
  },
  LAST_30_DAYS: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.LAST_30_DAYS],
    value: FilterDate.LAST_30_DAYS,
    getValue: () => {
      const yesterday = moment().subtract(1, 'days');
      const monthAgo = moment().subtract(30, 'days');
      return [monthAgo, yesterday];
    },
  },
  LAST_30_DAYS_INCLUSIVE: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.LAST_30_DAYS],
    value: FilterDate.LAST_30_DAYS,
    getValue: () => {
      const today = moment();
      const monthAgo = moment().subtract(29, 'days');
      return [monthAgo, today];
    },
  },
  THIS_MONTH: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.THIS_MONTH],
    value: FilterDate.THIS_MONTH,
    getValue: () => {
      const today = moment();
      const monthStart = moment().startOf('month');
      return [monthStart, today];
    },
  },
  LAST_MONTH: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.LAST_MONTH],
    value: FilterDate.LAST_MONTH,
    getValue: () => {
      const endOfLastMonth = moment().startOf('month').subtract(1, 'day');
      const startOfLastMonth = moment().startOf('month').subtract(1, 'day').startOf('month');
      return [startOfLastMonth, endOfLastMonth];
    },
  },
  THIS_YEAR: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.THIS_YEAR],
    value: FilterDate.THIS_YEAR,
    getValue: () => {
      const today = moment();
      const yearStart = moment().startOf('year');
      return [yearStart, today];
    },
  },
  ALL_TIME: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.ALL_TIME],
    value: FilterDate.ALL_TIME,
    getValue: () => {
      const today = moment();
      return [moment(new Date(0)), today];
    },
  },
  CUSTOM_RANGE: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.CUSTOM_RANGE],
    value: FilterDate.CUSTOM_RANGE,
    getValue: () => null,
  },
};

export const MONTH_PICKER_SHORTCUTS = [
  {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.LAST_MONTH],
    value: FilterDate.LAST_MONTH,
  },
  {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.THIS_MONTH],
    value: FilterDate.THIS_MONTH,
  },
  {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.CUSTOM_RANGE],
    value: FilterDate.CUSTOM_RANGE,
  },
];

//TODO: refactor shortcuts into a proper class
export const footTrafficShortcuts = (
  maxDate: Moment,
): Record<
  Extract<
    FilterDate,
    | FilterDate.LAST_7_DAYS
    | FilterDate.LAST_30_DAYS
    | FilterDate.THIS_MONTH
    | FilterDate.LAST_MONTH
    | FilterDate.THIS_YEAR
    | FilterDate.CUSTOM_RANGE
  >,
  ShortcutItem
> => ({
  [FilterDate.LAST_7_DAYS]: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.LAST_7_DAYS],
    value: FilterDate.LAST_7_DAYS,
    getValue: () => {
      const rangeEnd = maxDate.clone();
      const rangeStart = maxDate.clone().subtract(6, 'days');
      return [rangeStart, rangeEnd];
    },
  },
  [FilterDate.LAST_30_DAYS]: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.LAST_30_DAYS],
    value: FilterDate.LAST_30_DAYS,
    getValue: () => {
      const rangeEnd = maxDate.clone();
      const rangeStart = maxDate.clone().subtract(29, 'days');
      return [rangeStart, rangeEnd];
    },
  },
  [FilterDate.THIS_MONTH]: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.THIS_MONTH],
    value: FilterDate.THIS_MONTH,
    getValue: () => {
      const rangeEnd = maxDate.clone();
      const monthStart = maxDate.clone().startOf('month');
      return [monthStart, rangeEnd];
    },
  },
  [FilterDate.LAST_MONTH]: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.LAST_MONTH],
    value: FilterDate.LAST_MONTH,
    getValue: () => {
      const rangeEnd = maxDate.clone().startOf('month').subtract(1, 'day');
      const rangeStart = rangeEnd.clone().startOf('month');
      return [rangeStart, rangeEnd];
    },
  },
  [FilterDate.THIS_YEAR]: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.THIS_YEAR],
    value: FilterDate.THIS_YEAR,
    getValue: () => {
      const rangeEnd = maxDate.clone();
      const rangeStart = maxDate.clone().startOf('year');
      return [rangeStart, rangeEnd];
    },
  },
  [FilterDate.CUSTOM_RANGE]: {
    label: DATE_RANGE_FORMAT_LABELS[FilterDate.CUSTOM_RANGE],
    value: FilterDate.CUSTOM_RANGE,
    getValue: () => null,
  },
});
